export const HOME_WIDGET_KINDS = [
  'stat',
  'status-list',
  'chart',
  'mini-table',
  'event-feed',
  'cells',
  'week-grid',
  'quick-action',
  'note',
] as const;
export type HomeWidgetKind = (typeof HOME_WIDGET_KINDS)[number];
export const HOME_WIDGET_STATES = ['ready', 'loading', 'error', 'no-access'] as const;
export type HomeWidgetStateKind = (typeof HOME_WIDGET_STATES)[number];
export interface WidgetSize {
  columns: 3 | 6 | 9 | 12;
  rows: 1 | 2 | 3 | 4;
}
export type HomeApplicationIcon = 'box' | 'tasks' | 'calendar' | 'finance' | 'notes';
export interface HomeWidgetSettings {
  onlyMine: boolean;
  period: 'day' | 'week' | 'month';
}
export interface HomeWidgetDefinition {
  id: string;
  type: HomeWidgetKind;
  chartVariant?: 'bar' | 'line' | 'heatmap';
  title: string;
  application: { id: string; title: string; icon: HomeApplicationIcon };
  allowedSizes: readonly WidgetSize[];
  settings?: { onlyMine?: boolean; period?: boolean };
  showOpenApplication?: boolean;
}
export interface HomeWidgetInstance {
  instanceId: string;
  definitionId: string;
  size: WidgetSize;
  settings: HomeWidgetSettings;
}
export type HomeWidgetTone = 'neutral' | 'info' | 'success' | 'warn' | 'urgent';
interface WithTotal {
  total?: number;
}
export type HomeWidgetData =
  | {
      type: 'stat';
      label: string;
      value: string;
      unit?: string;
      delta?: { value: number; label?: string; goodDirection?: 'up' | 'down' };
      trend?: readonly number[];
    }
  | ({
      type: 'status-list';
      items: readonly {
        id: string;
        title: string;
        status: { label: string; tone: HomeWidgetTone };
        detail?: string;
      }[];
    } & WithTotal)
  | {
      type: 'chart';
      variant: 'bar';
      categories: readonly { key: string; label: string; longLabel?: string }[];
      series: readonly {
        id: string;
        label: string;
        values: readonly (number | null)[];
        color?: 1 | 2 | 3 | 4;
      }[];
    }
  | {
      type: 'chart';
      variant: 'line';
      series: readonly {
        id: string;
        label: string;
        points: readonly { x: number; y: number }[];
        color?: 1 | 2 | 3 | 4;
      }[];
      unit?: string;
    }
  | {
      type: 'chart';
      variant: 'heatmap';
      from: string;
      to: string;
      values: Readonly<Record<string, number | null>>;
      domainMax?: number;
    }
  | ({
      type: 'mini-table';
      columns: readonly { id: string; label: string; numeric?: boolean }[];
      rows: readonly { id: string; cells: Readonly<Record<string, string | number | null>> }[];
    } & WithTotal)
  | ({
      type: 'event-feed';
      items: readonly {
        id: string;
        title: string;
        at: string;
        actor?: string;
        kind: 'note' | 'change' | 'resolve' | 'alert';
      }[];
    } & WithTotal)
  | ({
      type: 'cells';
      items: readonly { id: string; label: string; status: 'ok' | 'warning' | 'error' | 'unknown' }[];
    } & WithTotal)
  | {
      type: 'week-grid';
      anchor: string;
      timeZone: string;
      rows: readonly { id: string; title: string }[];
      shifts: readonly {
        id: string;
        rowId: string;
        title: string;
        start: string;
        end: string;
        tone?: 'info' | 'success' | 'warning';
      }[];
    }
  | { type: 'quick-action'; label: string; buttonLabel: string; inputLabel?: string; placeholder?: string }
  | { type: 'note'; text: string; links?: readonly { id: string; label: string; href: string }[] };
export type HomeWidgetState =
  | {
      state: 'ready';
      data: HomeWidgetData;
      updatedAt?: string;
      attention?: { label: string; tone: 'info' | 'warn' };
    }
  | { state: 'loading' }
  | { state: 'error'; message?: string }
  | { state: 'no-access' };
const size = (columns: WidgetSize['columns'], rows: WidgetSize['rows']): WidgetSize => ({ columns, rows });
export const HOME_WIDGET_SIZES: Readonly<Record<HomeWidgetKind, readonly WidgetSize[]>> = {
  stat: [size(3, 1), size(3, 2), size(6, 1), size(6, 2)],
  'status-list': [size(3, 2), size(6, 2), size(6, 3), size(9, 3)],
  chart: [size(6, 2), size(6, 3), size(9, 3), size(12, 3), size(12, 4)],
  'mini-table': [size(6, 2), size(6, 3), size(9, 3), size(12, 4)],
  'event-feed': [size(3, 2), size(6, 2), size(6, 3), size(9, 3)],
  cells: [size(3, 2), size(6, 2), size(6, 3), size(9, 3)],
  'week-grid': [size(6, 3), size(9, 3), size(12, 3), size(12, 4)],
  'quick-action': [size(3, 1), size(6, 1), size(6, 2)],
  note: [size(3, 2), size(6, 2), size(6, 3), size(9, 3)],
};
export function homeWidgetKind(value: unknown): value is HomeWidgetKind {
  return typeof value === 'string' && (HOME_WIDGET_KINDS as readonly string[]).includes(value);
}
const record = (value: unknown): value is Record<string, unknown> =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);
const text = (value: unknown, max = 300): value is string =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= max;
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const optionalText = (value: unknown, max = 300) => value === undefined || text(value, max);
const array = (value: unknown): value is unknown[] => Array.isArray(value) && value.length <= 500;
const unique = (items: readonly unknown[], key: string) =>
  items.every(record) && new Set(items.map((item) => item[key])).size === items.length;
function json(value: unknown, depth = 0, seen = new Set<object>()): boolean {
  if (depth > 16) return false;
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value !== 'object' || seen.has(value)) return false;
  seen.add(value);
  const okay = Array.isArray(value)
    ? value.length <= 500 && value.every((item) => json(item, depth + 1, seen))
    : record(value) &&
      Object.values(value).every((item) => item === undefined || json(item, depth + 1, seen));
  seen.delete(value);
  return okay;
}
function day(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(value + 'T12:00:00Z')) &&
    new Date(value + 'T12:00:00Z').toISOString().slice(0, 10) === value
  );
}
function instant(value: unknown): value is string {
  return (
    typeof value === 'string' && /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(value) && Number.isFinite(Date.parse(value))
  );
}
export function homeWidgetSafeLink(value: unknown): value is string {
  if (
    !text(value, 2000) ||
    value.trim() !== value ||
    value.includes('\\') ||
    Array.from(value).some((ch) => ch.charCodeAt(0) < 32 || ch.charCodeAt(0) === 127)
  )
    return false;
  if (value.startsWith('/') && !value.startsWith('//')) return true;
  try {
    const url = new URL(value);
    return ['https:', 'http:'].includes(url.protocol) && !url.username && !url.password;
  } catch {
    return false;
  }
}
export function homeWidgetSizeError(kind: unknown, value: unknown): string | null {
  if (!homeWidgetKind(kind)) return 'Неизвестный тип виджета.';
  if (
    !record(value) ||
    Object.keys(value).some((key) => !['columns', 'rows'].includes(key)) ||
    !HOME_WIDGET_SIZES[kind].some((size) => size.columns === value.columns && size.rows === value.rows)
  )
    return 'Этот размер не поддерживается типом виджета.';
  return null;
}
export function homeWidgetSettingsError(value: unknown): string | null {
  return record(value) &&
    Object.keys(value).every((key) => ['onlyMine', 'period'].includes(key)) &&
    typeof value.onlyMine === 'boolean' &&
    ['day', 'week', 'month'].includes(String(value.period))
    ? null
    : 'Некорректные настройки виджета.';
}
export function homeWidgetDefinitionError(value: unknown): string | null {
  if (
    !record(value) ||
    !text(value.id, 120) ||
    !text(value.title, 160) ||
    !homeWidgetKind(value.type) ||
    !record(value.application) ||
    !text(value.application.id, 120) ||
    !text(value.application.title, 120) ||
    !['box', 'tasks', 'calendar', 'finance', 'notes'].includes(String(value.application.icon))
  )
    return 'Описание виджета неполное.';
  if (
    !Array.isArray(value.allowedSizes) ||
    !value.allowedSizes.length ||
    value.allowedSizes.length > 16 ||
    value.allowedSizes.some((item) => homeWidgetSizeError(value.type, item))
  )
    return 'Размеры описания не поддерживаются типом виджета.';
  const keys = value.allowedSizes.map(
    (item) => (item as WidgetSize).columns + 'x' + (item as WidgetSize).rows,
  );
  if (new Set(keys).size !== keys.length) return 'Размеры описания повторяются.';
  if (
    value.settings !== undefined &&
    (!record(value.settings) ||
      Object.keys(value.settings).some((key) => !['onlyMine', 'period'].includes(key)) ||
      Object.values(value.settings).some((item) => typeof item !== 'boolean'))
  )
    return 'Неизвестная настройка виджета.';
  if (
    value.chartVariant !== undefined &&
    (value.type !== 'chart' || !['bar', 'line', 'heatmap'].includes(String(value.chartVariant)))
  )
    return 'Неизвестный вид графика.';
  if (value.showOpenApplication !== undefined && typeof value.showOpenApplication !== 'boolean')
    return 'Некорректный переход в приложение.';
  return null;
}
export function homeWidgetDataError(value: unknown): string | null {
  if (!record(value) || !homeWidgetKind(value.type) || !json(value)) return 'Данные виджета недоступны.';
  const bad = 'Данные не соответствуют типу виджета.';
  const rows = (items: unknown, key = 'id'): items is Record<string, unknown>[] =>
    array(items) && unique(items, key);
  const total = (count: number) =>
    value.total === undefined || (Number.isSafeInteger(value.total) && Number(value.total) >= count);
  const tone = (item: unknown) => ['neutral', 'info', 'success', 'warn', 'urgent'].includes(String(item));
  switch (value.type) {
    case 'stat':
      return text(value.label) &&
        text(value.value, 120) &&
        optionalText(value.unit, 40) &&
        (value.trend === undefined || (array(value.trend) && value.trend.every(finite))) &&
        (value.delta === undefined ||
          (record(value.delta) &&
            finite(value.delta.value) &&
            optionalText(value.delta.label) &&
            [undefined, 'up', 'down'].includes(value.delta.goodDirection as undefined | 'up' | 'down')))
        ? null
        : bad;
    case 'status-list':
      return rows(value.items) &&
        value.items.every(
          (item) =>
            record(item) &&
            text(item.id, 120) &&
            text(item.title) &&
            optionalText(item.detail) &&
            record(item.status) &&
            text(item.status.label, 80) &&
            tone(item.status.tone),
        ) &&
        total(value.items.length)
        ? null
        : bad;
    case 'mini-table':
      return rows(value.columns) &&
        value.columns.length > 0 &&
        value.columns.length <= 12 &&
        value.columns.every(
          (item) =>
            record(item) &&
            text(item.id, 120) &&
            text(item.label, 80) &&
            (item.numeric === undefined || typeof item.numeric === 'boolean'),
        ) &&
        rows(value.rows) &&
        value.rows.every(
          (item) =>
            record(item) &&
            text(item.id, 120) &&
            record(item.cells) &&
            Object.values(item.cells).every(
              (cell) => cell === null || finite(cell) || (typeof cell === 'string' && cell.length <= 500),
            ),
        ) &&
        total(value.rows.length)
        ? null
        : bad;
    case 'event-feed':
      return rows(value.items) &&
        value.items.every(
          (item) =>
            record(item) &&
            text(item.id, 120) &&
            text(item.title) &&
            instant(item.at) &&
            optionalText(item.actor, 120) &&
            ['note', 'change', 'resolve', 'alert'].includes(String(item.kind)),
        ) &&
        total(value.items.length)
        ? null
        : bad;
    case 'cells':
      return rows(value.items) &&
        value.items.every(
          (item) =>
            record(item) &&
            text(item.id, 120) &&
            text(item.label, 120) &&
            ['ok', 'warning', 'error', 'unknown'].includes(String(item.status)),
        ) &&
        total(value.items.length)
        ? null
        : bad;
    case 'quick-action':
      return text(value.label) &&
        text(value.buttonLabel, 80) &&
        optionalText(value.inputLabel, 80) &&
        optionalText(value.placeholder, 160)
        ? null
        : bad;
    case 'note':
      return typeof value.text === 'string' &&
        value.text.length <= 20000 &&
        (value.links === undefined ||
          (rows(value.links) &&
            value.links.every(
              (item) =>
                record(item) && text(item.id, 120) && text(item.label, 160) && homeWidgetSafeLink(item.href),
            )))
        ? null
        : bad;
    case 'week-grid': {
      if (
        !day(value.anchor) ||
        !text(value.timeZone, 120) ||
        !rows(value.rows) ||
        value.rows.length > 20 ||
        !value.rows.every((item) => record(item) && text(item.id, 120) && text(item.title, 120)) ||
        !rows(value.shifts)
      )
        return bad;
      try {
        new Intl.DateTimeFormat('ru', { timeZone: value.timeZone });
      } catch {
        return bad;
      }
      const ids = new Set(value.rows.map((item) => (item as Record<string, unknown>).id));
      return value.shifts.every(
        (item) =>
          record(item) &&
          text(item.id, 120) &&
          ids.has(item.rowId) &&
          text(item.title) &&
          instant(item.start) &&
          instant(item.end) &&
          Date.parse(item.end) > Date.parse(item.start) &&
          [undefined, 'info', 'success', 'warning'].includes(
            item.tone as undefined | 'info' | 'success' | 'warning',
          ),
      )
        ? null
        : bad;
    }
    case 'chart': {
      if (value.variant === 'bar')
        return rows(value.categories, 'key') &&
          value.categories.every(
            (item) =>
              record(item) && text(item.key, 120) && text(item.label, 80) && optionalText(item.longLabel),
          ) &&
          rows(value.series) &&
          value.series.length <= 4 &&
          value.series.every(
            (item) =>
              record(item) &&
              text(item.id, 120) &&
              text(item.label, 120) &&
              array(item.values) &&
              item.values.length === (value.categories as unknown[]).length &&
              item.values.every((v) => v === null || finite(v)) &&
              [undefined, 1, 2, 3, 4].includes(item.color as undefined | number),
          )
          ? null
          : bad;
      if (value.variant === 'line')
        return rows(value.series) &&
          value.series.length <= 4 &&
          value.series.every(
            (item) =>
              record(item) &&
              text(item.id, 120) &&
              text(item.label, 120) &&
              array(item.points) &&
              item.points.every((p) => record(p) && finite(p.x) && finite(p.y)) &&
              [undefined, 1, 2, 3, 4].includes(item.color as undefined | number),
          ) &&
          optionalText(value.unit, 40)
          ? null
          : bad;
      if (value.variant === 'heatmap')
        return day(value.from) &&
          day(value.to) &&
          value.from <= value.to &&
          Date.parse(value.to) - Date.parse(value.from) <= 366 * 86400000 &&
          record(value.values) &&
          Object.entries(value.values).every(
            ([key, number]) => day(key) && (number === null || (finite(number) && number >= 0)),
          ) &&
          (value.domainMax === undefined || (finite(value.domainMax) && value.domainMax > 0))
          ? null
          : bad;
      return bad;
    }
  }
}
export function homeWidgetStateError(state: unknown, type?: HomeWidgetKind): string | null {
  if (!record(state) || !(HOME_WIDGET_STATES as readonly unknown[]).includes(state.state))
    return 'Неизвестное состояние виджета.';
  if (state.state === 'ready') {
    const issue = homeWidgetDataError(state.data);
    if (issue) return issue;
    if (type && (state.data as HomeWidgetData).type !== type) return 'Тип данных не совпадает с описанием.';
    if (state.updatedAt !== undefined && !instant(state.updatedAt)) return 'Некорректное время обновления.';
    if (
      state.attention !== undefined &&
      (!record(state.attention) ||
        !text(state.attention.label, 80) ||
        !['info', 'warn'].includes(String(state.attention.tone)))
    )
      return 'Некорректная метка внимания.';
  }
  return state.state === 'error' && !optionalText(state.message, 500)
    ? 'Некорректное сообщение об ошибке.'
    : null;
}
export function homeWidgetVisibleLimit(kind: HomeWidgetKind, size: WidgetSize): number {
  if (kind === 'cells') return Math.min(36, size.columns * size.rows * 2);
  return Math.max(2, Math.min(kind === 'mini-table' ? 8 : 6, size.rows * 2 - 1));
}
